import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { BG_HOST, BG_VIDEO_IDS, bgEnabled } from '../lib/bgVideo';
import { useServiceAvailable } from '../hooks/useServiceOrigin';
import './BackgroundVideo.css';

// Resolve a storage media id to its HLS master (preferred) or progressive MP4.
// HEAD is public + CORS-exposes X-HLS-URL (verified arkserver host).
async function resolveSrc(id: number): Promise<{ src: string; hls: boolean }> {
  const media = `${BG_HOST}/storage/media/${id}`;
  try {
    const res = await fetch(media, { method: 'HEAD' });
    const hls = res.headers.get('x-hls-url');
    if (hls) return { src: hls, hls: true };
  } catch { /* fall through to progressive */ }
  return { src: media, hls: false };
}

/**
 * Ambient video backdrop — a single heavily-blurred, frosted loop behind the
 * whole app (Alex 2026-06-23: "animierter Hintergrund mit Glassmorphism"). Only
 * mounts when enabled in Settings; rotates through the BGMOVIES clips every
 * ~70 s with a soft cross-fade. Muted/looped/autoplay so it never demands
 * attention; the frost layer keeps it subtle + the UI readable.
 */
export function BackgroundVideo() {
  // Deko-Video kommt aus einem Betreiber-Storage — eine Instanz, die Storage
  // mit null deklariert, telefoniert auch für Deko nirgendwohin (77eb0c9).
  const storageAvailable = useServiceAvailable('storage');
  const [on, setOn] = useState(bgEnabled());
  // Start on a random clip so it's not always the same one (app code — random ok).
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * BG_VIDEO_IDS.length));
  const [fading, setFading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  // React to the Settings toggle without a reload.
  useEffect(() => {
    const h = (e: Event) => setOn((e as CustomEvent<boolean>).detail);
    window.addEventListener('cloud-bg-change', h);
    return () => window.removeEventListener('cloud-bg-change', h);
  }, []);

  // Load / swap the source whenever the clip index changes (and we're on).
  useEffect(() => {
    if (!on || !storageAvailable) return;
    let cancelled = false;
    (async () => {
      const { src, hls } = await resolveSrc(BG_VIDEO_IDS[idx]);
      const vid = videoRef.current;
      if (cancelled || !vid) return;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      const nativeHls = vid.canPlayType('application/vnd.apple.mpegurl');
      if (hls && Hls.isSupported() && !nativeHls) {
        // Low start level — it's blurred to mush anyway, save CPU/bandwidth.
        const h = new Hls({ startLevel: 0, capLevelToPlayerSize: false, maxBufferLength: 10 });
        h.loadSource(src);
        h.attachMedia(vid);
        hlsRef.current = h;
      } else {
        vid.src = src;
      }
      vid.play().catch(() => { /* autoplay policies — muted should allow it */ });
      setFading(false);
    })();
    return () => { cancelled = true; };
  }, [on, idx, storageAvailable]);

  // Rotate clips on a slow timer with a fade through the frost.
  useEffect(() => {
    if (!on || !storageAvailable || BG_VIDEO_IDS.length < 2) return;
    const t = window.setInterval(() => {
      setFading(true);
      window.setTimeout(() => setIdx((i) => (i + 1) % BG_VIDEO_IDS.length), 1400);
    }, 70_000);
    return () => window.clearInterval(t);
  }, [on, storageAvailable]);

  useEffect(() => () => { if (hlsRef.current) hlsRef.current.destroy(); }, []);

  if (!on || !storageAvailable) return null;
  return (
    <div className="bgv" aria-hidden="true">
      <video
        ref={videoRef}
        className={`bgv-video${fading ? ' fading' : ''}`}
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
      />
      <div className="bgv-frost" />
    </div>
  );
}
